import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Trash2, X, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import type { EnrollmentWithSubscriber } from './AutomationEnrollmentsTab';

interface SendCounts {
  pending: number;
  sending: number;
  sent: number;
  failed: number;
  canceled: number;
}

interface CancelAutomationEnrollmentModalProps {
  isOpen: boolean;
  enrollment: EnrollmentWithSubscriber | null;
  automationName: string;
  onClose: () => void;
  onSuccess: () => void;
}

function subscriberLabel(e: EnrollmentWithSubscriber): string {
  return (
    e.subscriber?.email?.trim() ||
    e.subscriber?.name?.trim() ||
    'this subscriber'
  );
}

export default function CancelAutomationEnrollmentModal({
  isOpen,
  enrollment,
  automationName,
  onClose,
  onSuccess,
}: CancelAutomationEnrollmentModalProps) {
  const [deleteQueueRows, setDeleteQueueRows] = useState(false);
  const [removeEnrollmentRecord, setRemoveEnrollmentRecord] = useState(false);
  const [counts, setCounts] = useState<SendCounts | null>(null);
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadCounts = useCallback(async () => {
    if (!enrollment?.id) return;
    setLoadingCounts(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('scheduled_emails')
        .select('status')
        .eq('automation_enrollment_id', enrollment.id);
      if (err) throw err;
      const next: SendCounts = {
        pending: 0,
        sending: 0,
        sent: 0,
        failed: 0,
        canceled: 0,
      };
      for (const r of (data ?? []) as { status: string }[]) {
        const key = r.status as keyof SendCounts;
        if (key in next) next[key] += 1;
      }
      setCounts(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load scheduled sends');
      setCounts(null);
    } finally {
      setLoadingCounts(false);
    }
  }, [enrollment?.id]);

  useEffect(() => {
    if (!isOpen || !enrollment) return;
    setDeleteQueueRows(false);
    setRemoveEnrollmentRecord(false);
    setError(null);
    void loadCounts();
  }, [isOpen, enrollment, loadCounts]);

  useEffect(() => {
    if (removeEnrollmentRecord) setDeleteQueueRows(true);
  }, [removeEnrollmentRecord]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, submitting]);

  const queueCount = (counts?.pending ?? 0) + (counts?.sending ?? 0);
  const totalSendRows = counts
    ? counts.pending + counts.sending + counts.sent + counts.failed + counts.canceled
    : 0;

  const handleConfirm = async () => {
    if (!enrollment) return;
    setSubmitting(true);
    setError(null);
    try {
      if (removeEnrollmentRecord) {
        const { error: rpcErr } = await supabase.rpc('remove_automation_enrollment', {
          p_enrollment_id: enrollment.id,
        });
        if (rpcErr) throw rpcErr;
        toast.success('Enrollment and all linked scheduled sends removed');
      } else {
        const { error: rpcErr } = await supabase.rpc('cancel_automation_enrollment', {
          p_enrollment_id: enrollment.id,
        });
        if (rpcErr) throw rpcErr;

        let removed = 0;
        if (deleteQueueRows && queueCount > 0) {
          const { data, error: delErr } = await supabase
            .from('scheduled_emails')
            .delete()
            .eq('automation_enrollment_id', enrollment.id)
            .in('status', ['pending', 'sending'])
            .select('id');
          if (delErr) throw delErr;
          removed = data?.length ?? 0;
        }

        if (deleteQueueRows && removed > 0) {
          toast.success(
            `Enrollment ended — removed ${removed} queued send${removed === 1 ? '' : 's'} from the queue`,
          );
        } else {
          toast.success('Enrollment ended — pending sends will not go out');
        }
      }
      onSuccess();
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Action failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !enrollment) return null;

  const label = subscriberLabel(enrollment);

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-lg flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-enrollment-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2
            id="cancel-enrollment-title"
            className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"
          >
            <XCircle className="w-5 h-5 text-red-500" />
            End enrollment?
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 text-sm text-gray-700 dark:text-gray-200">
          <p>
            <span className="font-medium text-gray-900 dark:text-white">{label}</span> in{' '}
            <span className="font-medium">{automationName}</span>.
          </p>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 px-4 py-3 text-xs space-y-2">
            <p className="text-gray-700 dark:text-gray-200">
              <span className="font-medium text-gray-900 dark:text-white">Enrollment linkage</span>{' '}
              is the row in <code className="text-[11px]">automation_enrollments</code> (subscriber ↔
              automation). <span className="font-medium">Scheduled emails</span> are queue/history rows
              that point at that enrollment — deleting sends alone does not unenroll someone.
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              <span className="font-medium">End enrollment</span> sets the enrollment to{' '}
              <span className="font-medium">canceled</span> so no more steps run. Optional cleanup
              below only affects scheduled email rows.
            </p>
          </div>

          {loadingCounts ? (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-2">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
              Loading scheduled sends…
            </div>
          ) : counts ? (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 px-4 py-3 text-xs space-y-1">
              <div className="font-medium text-gray-900 dark:text-white uppercase tracking-wide">
                Scheduled sends for this enrollment
              </div>
              <div>
                <span className="font-medium">{queueCount}</span> in queue
                {counts.sending > 0 ? ` (${counts.sending} sending)` : ''}
              </div>
              <div>
                <span className="font-medium">{counts.sent}</span> sent
                {counts.failed + counts.canceled > 0
                  ? ` · ${counts.failed} failed · ${counts.canceled} canceled`
                  : ''}
              </div>
              <div className="text-gray-500 dark:text-gray-400">
                {totalSendRows} row{totalSendRows === 1 ? '' : 's'} total linked to this enrollment
              </div>
            </div>
          ) : null}

          {queueCount > 0 && !removeEnrollmentRecord ? (
            <label className="flex gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900/30">
              <input
                type="checkbox"
                className="mt-1"
                checked={deleteQueueRows}
                onChange={(e) => setDeleteQueueRows(e.target.checked)}
              />
              <span>
                <span className="font-medium text-gray-900 dark:text-white flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete {queueCount} queued row{queueCount === 1 ? '' : 's'} (optional)
                </span>
                <span className="block mt-1 text-gray-600 dark:text-gray-300">
                  Enrollment is still ended without this — pending rows are marked canceled. Check
                  this to remove queue rows from the Scheduled sends tab.
                </span>
              </span>
            </label>
          ) : null}

          <label className="flex gap-3 p-3 rounded-lg border border-red-200 dark:border-red-800 cursor-pointer hover:bg-red-50/50 dark:hover:bg-red-950/20">
            <input
              type="checkbox"
              className="mt-1"
              checked={removeEnrollmentRecord}
              onChange={(e) => setRemoveEnrollmentRecord(e.target.checked)}
            />
            <span>
              <span className="font-medium text-gray-900 dark:text-white">
                Remove enrollment record completely
              </span>
              <span className="block mt-1 text-gray-600 dark:text-gray-300">
                Deletes the enrollment row and all {totalSendRows || 'linked'} scheduled send
                {totalSendRows === 1 ? '' : 's'} (including sent history). Subscriber disappears
                from the Enrollments list. They can be enrolled again fresh.
              </span>
            </span>
          </label>

          <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-900/20 px-3 py-2 flex gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 dark:text-amber-100">
              Cannot undo emails already delivered via Postmark. Full remove deletes DB history only.
            </p>
          </div>

          {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
        </div>

        <div className="shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Keep enrollment
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={submitting || loadingCounts}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg transition-colors"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {submitting
              ? 'Working…'
              : removeEnrollmentRecord
                ? 'Remove enrollment'
                : 'End enrollment'}
          </button>
        </div>
      </div>
    </div>
  );
}
